import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Logo } from './Logo';
import { QuizQuestion, Option } from './QuizQuestion';
import { QuizResults } from './QuizResults';
import { ProgressBar } from './ProgressBar';
import { CTAScreen } from './CTAScreen';
import { BookOpen, X, FileText } from 'lucide-react';
// Quiz Data
const QUIZ_DATA = [{
  id: 'q1',
  question: 'Why is antimicrobial resistance (AMR) a major global health threat?',
  imageUrl: 'https://images.unsplash.com/photo-1581594693702-fbdc51b2763b?w=800&h=400&fit=crop&crop=faces',
  imageAlt: 'Black male doctor examining a child patient',
  options: [{
    id: 'a',
    text: 'It mainly affects developing countries'
  }, {
    id: 'b',
    text: 'It increases illness burden, deaths, and hospital stays'
  }, {
    id: 'c',
    text: 'It reduces the effectiveness of vaccines'
  }],
  correctAnswerId: 'b',
  percentages: {
    a: 18,
    b: 67,
    c: 15
  },
  feedbackTitle: '',
  feedbackBody: 'Antimicrobial resistance (AMR) is a major global health threat and is associated with higher infection rates, increased mortality and longer hospital stays.\n\nAppropriate antibiotic selection based on susceptibility remains key to achieving positive clinical outcomes and helping limit the development of resistance.',
  references: ''
}, {
  id: 'q2',
  question: 'How does antibiotic surveillance help combat AMR?',
  imageUrl: 'https://images.unsplash.com/photo-1584820927498-cfe5211fd8bf?w=800&h=400&fit=crop&crop=faces',
  imageAlt: 'Black African scientist working in laboratory',
  options: [{
    id: 'a',
    text: 'By reducing hospital costs'
  }, {
    id: 'b',
    text: 'By providing insights to guide rational empirical antibiotic prescribing'
  }, {
    id: 'c',
    text: 'By eliminating the need for antibiotics'
  }],
  correctAnswerId: 'b',
  percentages: {
    a: 12,
    b: 72,
    c: 16
  },
  feedbackTitle: '',
  feedbackBody: "Antibiotic surveillance provides the insights needed to guide rational empirical antibiotic prescribing.² That's why GSK established the Survey of Antibiotic Resistance (SOAR) in 2002 — to generate robust local susceptibility data that supports effective empirical therapy and helps tackle AMR.¹ ³ ⁴\n\nToday, SOAR delivers real-world resistance insights from 11,000+ isolates (2023), 95 centres, 34 countries¹ ³ ⁴ and continues to monitor resistance trends worldwide.¹ ³ ⁴",
  references: '1. Torumkuney D, et al. J Antimicrob Chemother. 2016;71:i1–101.\n2. Ryu S, et al. JAC Antimicrob Resist. 2019;1:1–5.\n3. Global initiative for chronic obstructive lung disease, 2024 report. Available from: https://goldcopd.org/2024-gold-report/ Accessed October 2024.\n4. Stevens DL, et al. Clin Infect Dis. 2014;59:e10–52.'
}, {
  id: 'q3',
  question: "Which of the following best describes Augmentin's value in the context of rising antibiotic resistance?",
  imageUrl: 'https://images.unsplash.com/photo-1587854692152-cbe660dbde88?w=800&h=400&fit=crop',
  imageAlt: 'Pharmaceutical pills and capsules representing antibiotic treatment options',
  options: [{
    id: 'a',
    text: 'The susceptibility of key CA-RTI pathogens, S. pneumoniae and H. influenzae'
  }, {
    id: 'b',
    text: 'Amoxicillin-clavulanate is also recommended by international guidelines for a range of indicated bacterial infections'
  }, {
    id: 'c',
    text: 'Augmentin formulations enable you to customize route of administration and dosing per patient, across a range of bacterial infection severities and types'
  }, {
    id: 'd',
    text: 'All the above'
  }],
  correctAnswerId: 'd',
  percentages: {
    a: 14,
    b: 18,
    c: 11,
    d: 57
  },
  feedbackTitle: '',
  feedbackBody: 'Across infection severities and types⁵–⁷, Augmentin formulations let you tailor route of administration and dosing to the patient.*⁵–⁷ Amoxicillin-clavulanate is recommended in international guidelines for a range of indicated bacterial infections†⁸–¹²,¹³–¹⁵ and is listed as an "Access" antibiotic on the World Health Organization\'s Essential Medicines List.¹⁶',
  references: '5. Augmentin: Amoxicillin + clavulanic acid suspension. International Prescribing Information. Version number: GDS29/IPI17.\n6. Augmentin: Amoxicillin + clavulanic acid extra strength. International Prescribing Information. Version number: GDS29/IPI14.\n7. Augmentin Amoxicillin + clavulanic acid BD tablets. International Prescribing Information. Version number: GDS29/IPI16.\n8. Chronic obstructive pulmonary disease (acute exacerbation): antimicrobial prescribing. NICE guideline, 2018. Available from: https://www.nice.org.uk/guidance/ng114/resources/chronicobstructive-pulmonary-diseaseacuteexacerbation-antimicrobial-prescribingpdf-66141598418629 Accessed October 2024.\n9. Pneumonia (community acquired): antimicrobial prescribing. NICE guideline, 2019. Available from: https://www.nice.org.uk/guidance/ng138/resources/pneumonia-communityacquiredantimicrobialprescribingpdf-66141726069445 Accessed October 2024.\n10. Rosenfeld RM, et al. Otolaryngol Head Neck Surg. 2015;152:S1–39.\n11. Chow AW, et al. Clin Infect Dis. 2012;54:e72–112.\n12. Shulman ST, et al. Clin Infect Dis. 2012;55:e86–102.\n13. Lipsky BA, et al. Clin Infect Dis. 2012;54:e132–173.\n14. Cellulitis and erysipelas: antimicrobial prescribing. NICE guideline, 2019. Available from: https://www.nice.org.uk/guidance/ng141/resources/cellulitis-and-erysipelas-antimicrobialprescribingpdf-66141774778309 Accessed October 2024.\n15. Cellulitis and erysipelas: antimicrobial prescribing. NICE guideline, 2019. Available from: https://www.nice.org.uk/guidance/ng141/resources/cellulitis-and-erysipelasantimicrobialprescribingpdf-66141774778309 Accessed October 2024.\n16. World Health Organization. 23rd WHO Essential Medicines List, 2023. Available from: https://iris.who.int/bitstream/handle/10665/371090/WHO-MHP-HPS-EML-2023.02-eng.pdf?sequence=1 Accessed October 2024.\n\n*Please refer to local susceptibility data as susceptibility may vary with geography and time.⁵–⁷  14:1 formulation indicated for S. pneumoniae infections with penicillin minimum inhibitory concentration less than or equal to 4 μg/mL.⁶ † Indications include Acute Bacterial Rhinosinusitis, Acute Exacerbation of Chronic Obstructive Pulmonary Disease, Acute Otitis Media, Community-Acquired Pneumonia, Recurrent Tonsillopharyngitis, and Skin and Soft Tissue Infections.⁵–⁷'
}];
type AppState = 'quiz' | 'cta';
type ViewState = 'question' | 'results';
export function QuizApp() {
  const [appState, setAppState] = useState<AppState>('quiz');
  const [viewState, setViewState] = useState<ViewState>('question');
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(null);
  const [showReferences, setShowReferences] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const handleAnswer = (optionId: string) => {
    setSelectedOptionId(optionId);
    setViewState('results');
  };
  const handleNextQuestion = () => {
    if (currentQuestionIndex < QUIZ_DATA.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
      setViewState('question');
      setSelectedOptionId(null);
      setShowReferences(false);
    } else {
      setAppState('cta');
    }
  };
  const currentQuestion = QUIZ_DATA[currentQuestionIndex];
  return <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4" data-id="element-15">
      <div className="w-full max-w-5xl aspect-video bg-white text-text font-sans selection:bg-brand-primary selection:text-white overflow-hidden rounded-xl shadow-2xl flex flex-col relative" data-id="element-16">
        {/* Top brand bar */}
        <div className="w-full h-1.5 bg-brand-primary shrink-0" data-id="element-17"></div>

        <div className="relative z-10 flex-1 flex flex-col px-6 py-3 overflow-hidden" data-id="element-18">
          {/* Header Logo Area */}
          <header className="flex justify-between items-center mb-2 shrink-0" data-id="element-19">
            <Logo type="augmentin" width={120} data-id="element-20" />
            <Logo type="gsk" width={60} data-id="element-21" />
          </header>

          <main className="flex-grow flex flex-col justify-center min-h-0" data-id="element-22">
            <AnimatePresence mode="wait" data-id="element-23">
              {appState === 'quiz' && viewState === 'question' && <motion.div key={`question-${currentQuestionIndex}`} initial={{
              opacity: 0,
              x: 50
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -50
            }} transition={{
              duration: 0.4
            }} className="w-full" data-id="element-24">
                  <ProgressBar current={currentQuestionIndex + 1} total={QUIZ_DATA.length} data-id="element-25" />
                  <QuizQuestion question={currentQuestion.question} options={currentQuestion.options} onAnswer={handleAnswer} imageUrl={currentQuestion.imageUrl} imageAlt={currentQuestion.imageAlt} compact={currentQuestion.options.length > 3} data-id="element-26" />
                </motion.div>}

              {appState === 'quiz' && viewState === 'results' && selectedOptionId && <motion.div key={`results-${currentQuestionIndex}`} initial={{
              opacity: 0,
              x: 50
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -50
            }} transition={{
              duration: 0.4
            }} className="w-full" data-id="element-27">
                    <ProgressBar current={currentQuestionIndex + 1} total={QUIZ_DATA.length} data-id="element-28" />
                    <QuizResults question={currentQuestion.question} options={currentQuestion.options} selectedOptionId={selectedOptionId} correctAnswerId={currentQuestion.correctAnswerId} percentages={currentQuestion.percentages} feedbackTitle={currentQuestion.feedbackTitle} feedbackBody={currentQuestion.feedbackBody} onNext={handleNextQuestion} isLastQuestion={currentQuestionIndex === QUIZ_DATA.length - 1} compact={currentQuestion.options.length > 3} data-id="element-29" />
                  </motion.div>}

              {appState === 'cta' && <CTAScreen key="cta" onShowDisclaimer={() => setShowDisclaimer(true)} data-id="element-30" />}
            </AnimatePresence>
          </main>

          <footer className="mt-2 text-center shrink-0" data-id="element-31">
            {appState === 'quiz' && viewState === 'results' && currentQuestion.references ? <button onClick={() => setShowReferences(true)} className="inline-flex items-center gap-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider hover:text-brand-primary transition-colors" data-id="element-32">
                <BookOpen className="w-3 h-3" data-id="element-33" />
                View References
              </button> : <div className="text-[9px] text-gray-400" data-id="element-34">
                <p data-id="element-35">
                  © 2024 GSK group of companies or its licensor. All rights
                  reserved.
                </p>
                <p className="mt-0.5" data-id="element-36">
                  Trademarks are owned by or licensed to the GSK group of
                  companies.
                </p>
              </div>}
          </footer>
        </div>

        {/* References Modal Overlay */}
        <AnimatePresence data-id="element-37">
          {showReferences && currentQuestion.references && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.2
        }} className="absolute inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => setShowReferences(false)} data-id="element-38">
              <motion.div initial={{
            opacity: 0,
            scale: 0.95,
            y: 10
          }} animate={{
            opacity: 1,
            scale: 1,
            y: 0
          }} exit={{
            opacity: 0,
            scale: 0.95,
            y: 10
          }} transition={{
            duration: 0.2
          }} className="bg-white rounded-xl shadow-2xl mx-6 max-w-2xl w-full max-h-[80%] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-39">
                <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 shrink-0" data-id="element-40">
                  <h3 className="text-sm font-bold text-brand-primary flex items-center gap-2" data-id="element-41">
                    <BookOpen className="w-4 h-4" data-id="element-42" />
                    References
                  </h3>
                  <button onClick={() => setShowReferences(false)} className="p-1 rounded-full hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600" data-id="element-43">
                    <X className="w-4 h-4" data-id="element-44" />
                  </button>
                </div>
                <div className="px-5 py-4 overflow-y-auto" data-id="element-45">
                  <div className="text-xs text-gray-500 leading-relaxed whitespace-pre-line" data-id="element-46">
                    {currentQuestion.references}
                  </div>
                </div>
              </motion.div>
            </motion.div>}
        </AnimatePresence>

        {/* Legal Disclaimer Modal Overlay */}
        <AnimatePresence data-id="element-47">
          {showDisclaimer && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.2
        }} className="absolute inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => setShowDisclaimer(false)} data-id="element-48">
              <motion.div initial={{
            opacity: 0,
            scale: 0.95,
            y: 10
          }} animate={{
            opacity: 1,
            scale: 1,
            y: 0
          }} exit={{
            opacity: 0,
            scale: 0.95,
            y: 10
          }} transition={{
            duration: 0.2
          }} className="bg-white rounded-xl shadow-2xl mx-6 max-w-2xl w-full max-h-[80%] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-49">
                <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 shrink-0" data-id="element-50">
                  <h3 className="text-sm font-bold text-brand-primary flex items-center gap-2" data-id="element-51">
                    <FileText className="w-4 h-4" data-id="element-52" />
                    Legal Disclaimer
                  </h3>
                  <button onClick={() => setShowDisclaimer(false)} className="p-1 rounded-full hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600" data-id="element-53">
                    <X className="w-4 h-4" data-id="element-54" />
                  </button>
                </div>
                <div className="px-5 py-4 overflow-y-auto" data-id="element-55">
                  <div className="text-xs text-gray-600 leading-relaxed space-y-3" data-id="element-56">
                    <p data-id="element-57">
                      This is placeholder text and should be supplied per
                      country localisation.
                    </p>
                  </div>
                </div>
              </motion.div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
}